import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, List } from "lucide-react";
import { fetchWfExceptions } from "../../../Moduels/attendance/wfApi";
import { resolveEmployeeDisplay } from "../../common/displayUtils";
import { useEmployees } from "../../../hooks/useEmployees";
import MissingPunchCalendar from "../../attendance/MissingPunchCalendar";
import API from "../../../services/api";

const pad = (n) => String(n).padStart(2, "0");

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function monthToDateRange() {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  return {
    from: `${y}-${pad(m)}-01`,
    to: todayIso(),
  };
}

function yearToDateRange() {
  const d = new Date();
  return {
    from: `${d.getFullYear()}-01-01`,
    to: todayIso(),
  };
}

function formatPeriod(from, to) {
  if (!from || !to) return "";
  const opts = { day: "numeric", month: "short", year: "numeric" };
  const f = new Date(`${from}T00:00:00`).toLocaleDateString(undefined, opts);
  const t = new Date(`${to}T00:00:00`).toLocaleDateString(undefined, opts);
  return `${f} – ${t}`;
}

const RANGE_PRESETS = [
  { id: "month", label: "This month", hint: "1st of month to today" },
  { id: "year", label: "This year", hint: "1 Jan to today" },
];

export default function WfExceptions() {
  const { employees } = useEmployees();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("open");
  const [rangePreset, setRangePreset] = useState("month");
  const [viewMode, setViewMode] = useState("calendar");
  const [selectedDate, setSelectedDate] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);

  const dateRange = useMemo(
    () => (rangePreset === "year" ? yearToDateRange() : monthToDateRange()),
    [rangePreset]
  );

  const load = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const data = await fetchWfExceptions({
        statusFilter: filter || undefined,
        fromDate: dateRange.from,
        toDate: dateRange.to,
        exceptionType: "missing_punch",
      });
      setRows(data);
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [filter, dateRange.from, dateRange.to]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedDate("");
  }, [rangePreset, filter]);

  const displayedRows = useMemo(() => {
    if (!selectedDate) return rows;
    return rows.filter((r) => String(r.work_date).slice(0, 10) === selectedDate);
  }, [rows, selectedDate]);

  const resolve = async (row) => {
    const id = row.exception_id;
    const workDate = String(row.work_date).slice(0, 10);
    setResolvingId(id);
    setError("");
    setRows((prev) => {
      const next = prev.filter((r) => r.exception_id !== id);
      if (selectedDate === workDate) {
        const remaining = next.filter((r) => String(r.work_date).slice(0, 10) === workDate);
        if (remaining.length === 0) setSelectedDate("");
      }
      return next;
    });
    try {
      await API.post(`/wf/exceptions/${id}/resolve`, {
        resolution_type: "hr_approved",
        notes: "Resolved from missing punches inbox",
      });
    } catch (e) {
      setRows((prev) => {
        if (prev.some((r) => r.exception_id === id)) return prev;
        return [...prev, row].sort(
          (a, b) => String(b.work_date).localeCompare(String(a.work_date))
        );
      });
      setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : e?.response?.data?.detail?.message || e.message || "Resolve failed");
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div className="p-6 w-full max-w-full min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Missing Punches</h1>
          <p className="text-sm text-slate-500 mt-1">
            Calendar view of missing punch exceptions · {formatPeriod(dateRange.from, dateRange.to)}
          </p>
        </div>
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
          <button
            type="button"
            onClick={() => setViewMode("calendar")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium ${
              viewMode === "calendar" ? "bg-white text-teal-700 shadow-sm" : "text-slate-600"
            }`}
          >
            <CalendarDays className="w-4 h-4" />
            Calendar
          </button>
          <button
            type="button"
            onClick={() => setViewMode("list")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium ${
              viewMode === "list" ? "bg-white text-teal-700 shadow-sm" : "text-slate-600"
            }`}
          >
            <List className="w-4 h-4" />
            List
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {RANGE_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            onClick={() => setRangePreset(preset.id)}
            title={preset.hint}
            className={`px-3 py-1.5 rounded-full text-sm border ${
              rangePreset === preset.id
                ? "bg-teal-600 text-white border-teal-600"
                : "bg-white border-slate-200 text-slate-700 hover:border-teal-300"
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-4">
        {["open", "resolved", ""].map((f) => (
          <button
            key={f || "all"}
            type="button"
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-sm border ${
              filter === f ? "bg-teal-600 text-white border-teal-600" : "bg-white border-slate-200"
            }`}
          >
            {f || "all"}
          </button>
        ))}
      </div>

      {error && <div className="mb-3 text-red-600 text-sm">{error}</div>}

      {viewMode === "calendar" ? (
        <div className="mb-6">
          {loading ? (
            <div className="text-sm text-slate-500 py-8 text-center">Loading missing punches…</div>
          ) : (
            <MissingPunchCalendar
              fromDate={dateRange.from}
              toDate={dateRange.to}
              rows={rows}
              selectedDate={selectedDate}
              onDaySelect={setSelectedDate}
            />
          )}
        </div>
      ) : null}

      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-slate-800">
            {selectedDate
              ? `Missing punches on ${new Date(`${selectedDate}T00:00:00`).toLocaleDateString(undefined, {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}`
              : "All missing punches in range"}
          </h2>
          {selectedDate ? (
            <button
              type="button"
              onClick={() => setSelectedDate("")}
              className="text-xs text-teal-700 hover:underline"
            >
              Clear day filter
            </button>
          ) : null}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Employee</th>
              <th className="p-3 text-left">Type</th>
              <th className="p-3 text-left">Severity</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="p-4 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : displayedRows.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-4 text-center text-gray-500">
                  {selectedDate ? "No missing punches on this day." : "No missing punches in this range."}
                </td>
              </tr>
            ) : (
              displayedRows.map((r) => (
                <tr key={r.exception_id} className="border-t">
                  <td className="p-3">{r.work_date}</td>
                  <td className="p-3">{resolveEmployeeDisplay(r, employees)}</td>
                  <td className="p-3">{r.exception_type}</td>
                  <td className="p-3">{r.severity}</td>
                  <td className="p-3">{r.status}</td>
                  <td className="p-3 text-right">
                    {r.status === "open" && (
                      <button
                        type="button"
                        disabled={resolvingId === r.exception_id}
                        onClick={() => resolve(r)}
                        className="text-teal-700 underline disabled:opacity-50 disabled:no-underline"
                      >
                        {resolvingId === r.exception_id ? "Resolving…" : "Resolve"}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
